import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { useScopes } from '@textile-erp/core';
import { ProtectedRoute } from './ProtectedRoute';

interface ScopeRouteProps {
  children: React.ReactNode;
  /** Menu path the page belongs to, e.g. '/payroll/employee' */
  path: string;
  /** Scope display names on that menu; the user needs at least one of them */
  anyOf: string[];
}

/**
 * Route guard for form/sub pages that have no scope check of their own.
 * ProtectedRoute handles menu access; this adds the scope (Add / Edit / ...) check,
 * so a page cannot be opened just by typing its URL.
 */
export function ScopeRoute({ children, path, anyOf }: ScopeRouteProps) {
  return (
    <ProtectedRoute path={path}>
      <ScopeCheck path={path} anyOf={anyOf}>{children}</ScopeCheck>
    </ProtectedRoute>
  );
}

function ScopeCheck({ children, path, anyOf }: ScopeRouteProps) {
  const { hasScope, isLoading } = useScopes(path);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!anyOf.some((scope) => hasScope(scope))) {
    return <AccessDenied />;
  }

  return <>{children}</>;
}

/** Shown when the user opens a page (by URL) they have no permission for */
export function AccessDenied() {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center justify-center h-full p-8">
      <AlertCircle className="w-16 h-16 text-gray-400 mb-4" />
      <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">Access Denied</h2>
      <p className="text-gray-500 dark:text-gray-400 text-center max-w-md">
        You don't have permission to access this page. Please contact your administrator.
      </p>
      <button onClick={() => navigate(-1)} className="mt-6 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
        Go Back
      </button>
    </div>
  );
}
